import fastGlob from 'fast-glob';
import fs from 'fs';
import path from 'path';
import yaml from 'js-yaml';
import logger from '../../../../utilities/logger';
import type { ProjectConfig } from '../../../../types';
import type { IPackageJson } from 'package-json-type';

type WorkspaceType = 'npm' | 'pnpm';

type DependencySection =
  | 'dependencies'
  | 'optionalDependencies'
  | 'devDependencies';

interface WorkspacePackage {
  dir: string;
  manifest: IPackageJson;
  name: string;
  realDir: string;
}

interface DependencyReference {
  name: string;
  spec: string;
  section: DependencySection;
}

interface WorkspaceBundleInput {
  appPath: string;
  appPkgJson: IPackageJson;
  config: ProjectConfig;
}

export interface ZipEntry {
  content?: string | Buffer;
  from?: string;
  mode?: number;
  to: string;
}

export interface WorkspaceBundleResult {
  appPackageJson: IPackageJson;
  entries: ZipEntry[];
  shouldBundle: boolean;
}

interface WorkspaceDefinition {
  globs: string[];
  rootDir: string;
  realRootDir: string;
  type: WorkspaceType;
  warning?: string;
  catalog?: Record<string, string>;
}

interface PackageRewriteContext {
  bundlePath: string;
  packageName: string;
}

const BUNDLED_ROOT = 'bundledPackages';
const DEFAULT_IGNORE_GLOBS = [
  '**/node_modules/**',
  '**/.git/**',
  '**/.hg/**',
  '**/.svn/**',
  '**/.DS_Store',
  '**/npm-debug.log',
  '**/yarn-error.log',
];

export default async function prepareWorkspaceBundle(
  input: WorkspaceBundleInput,
): Promise<WorkspaceBundleResult> {
  const { config, appPkgJson, appPath } = input;
  if (!config.bundleWorkspacePackages?.enabled) {
    return {
      appPackageJson: appPkgJson,
      entries: [],
      shouldBundle: false,
    };
  }

  const workspace = findWorkspaceDefinition(appPath, appPkgJson);
  if (!workspace) {
    throw new Error(
      'Bundle workspace packages is enabled but no workspace root could be found.',
    );
  }

  if (workspace.warning) {
    logger.warn(workspace.warning);
  }

  const workspacePackages = collectWorkspacePackages(workspace);
  const bundledPackageNames = determineBundleClosure({
    appPath,
    appPkgJson,
    workspace,
    workspacePackages,
  });

  if (!bundledPackageNames.size) {
    logger.info('No workspace packages matched bundle criteria.');
    // Still resolve catalog references even when no workspace packages to bundle
    const rewrittenAppPkgJson = applyRewriteMap(
      appPkgJson,
      {},
      workspace.catalog,
    );
    return {
      appPackageJson: rewrittenAppPkgJson,
      entries: [],
      shouldBundle: false,
    };
  }

  const bundleEntries: ZipEntry[] = [];
  const appRewriteMap: Record<string, string> = {};
  const bundledPackageContexts = new Map<string, PackageRewriteContext>();

  const sortedPackageNames = Array.from(bundledPackageNames).sort();
  for (const packageName of sortedPackageNames) {
    const pkg = workspacePackages.get(packageName);
    if (!pkg) {
      throw new Error(
        `Workspace package "${packageName}" referenced for bundling but not found in workspace map.`,
      );
    }
    const bundlePath = getBundlePath(packageName);
    bundledPackageContexts.set(packageName, { bundlePath, packageName });
    appRewriteMap[packageName] = `file:../${path.posix.join(
      BUNDLED_ROOT,
      bundlePath,
    )}`;

    // eslint-disable-next-line no-await-in-loop -- sequential: later iterations depend on bundledPackageContexts
    const packageEntries = await buildPackageEntries({
      bundlePath,
      workspacePackage: pkg,
      bundledNames: bundledPackageNames,
      contexts: bundledPackageContexts,
      workspace,
    });
    bundleEntries.push(...packageEntries.files);
    bundleEntries.push(packageEntries.packageJsonEntry);
  }

  const rewrittenAppPkgJson = applyRewriteMap(
    appPkgJson,
    appRewriteMap,
    workspace.catalog,
  );

  logger.info(
    `Bundling ${sortedPackageNames.length} workspace package(s) from ${workspace.type} workspace at ${workspace.rootDir}`,
  );
  logger.info(`Bundled packages: ${sortedPackageNames.join(', ')}`);

  return {
    appPackageJson: rewrittenAppPkgJson,
    entries: bundleEntries,
    shouldBundle: true,
  };
}

function findWorkspaceDefinition(
  appPath: string,
  appPkgJson: IPackageJson,
): WorkspaceDefinition | undefined {
  const appDir = path.resolve(appPath);
  let currentDir = appDir;
  let previousDir = '';
  const pnpmPreference = detectPnpmPreference(appPkgJson);

  while (currentDir !== previousDir) {
    const pnpmFile = path.join(currentDir, 'pnpm-workspace.yaml');
    const packageJsonPath = path.join(currentDir, 'package.json');

    const hasPnpm = fs.existsSync(pnpmFile);
    const packageJson = readPackageJsonIfExists(packageJsonPath);
    const npmWorkspaces = getNpmWorkspaces(packageJson);

    if (hasPnpm || npmWorkspaces.length) {
      const pnpmDefinition = hasPnpm
        ? createPnpmDefinition({ pnpmFile, rootDir: currentDir })
        : undefined;
      const npmDefinition = npmWorkspaces.length
        ? createNpmDefinition({ globs: npmWorkspaces, rootDir: currentDir })
        : undefined;

      const chosen = chooseWorkspaceDefinition({
        pnpmPreference,
        pnpmDefinition,
        npmDefinition,
      });

      if (chosen) {
        const includesApp = workspaceIncludesPath({
          appDir,
          globs: chosen.globs,
          rootDir: chosen.rootDir,
        });
        if (includesApp) {
          return chosen;
        }
      }
    }

    previousDir = currentDir;
    currentDir = path.dirname(currentDir);
  }

  return undefined;
}

function detectPnpmPreference(appPkgJson: IPackageJson): boolean {
  const pkgManager = appPkgJson.packageManager;
  if (typeof pkgManager !== 'string') {
    return false;
  }
  // pnpm takes precedence, but bun also uses npm-style workspaces
  // so we don't need special handling for bun here
  return pkgManager.startsWith('pnpm@');
}

function readPackageJsonIfExists(filePath: string): IPackageJson | undefined {
  if (!fs.existsSync(filePath)) {
    return undefined;
  }
  try {
    return JSON.parse(fs.readFileSync(filePath, 'utf8'));
  } catch (error) {
    throw new Error(`Unable to parse package.json at ${filePath}: ${error}`);
  }
}

function getNpmWorkspaces(packageJson?: IPackageJson): string[] {
  if (!packageJson) {
    return [];
  }
  const { workspaces } = packageJson as { workspaces?: unknown };
  if (!workspaces) {
    return [];
  }
  if (Array.isArray(workspaces)) {
    return workspaces.filter(
      (value): value is string => typeof value === 'string',
    );
  }
  if (
    typeof workspaces === 'object' &&
    workspaces !== null &&
    'packages' in workspaces &&
    Array.isArray(workspaces.packages)
  ) {
    return workspaces.packages.filter(
      (value): value is string => typeof value === 'string',
    );
  }
  return [];
}

function createPnpmDefinition({
  pnpmFile,
  rootDir,
}: {
  pnpmFile: string;
  rootDir: string;
}): WorkspaceDefinition {
  const { packages, catalog } = parsePnpmWorkspaceFile(pnpmFile);
  if (!packages.length) {
    throw new Error(
      `pnpm workspace file at ${pnpmFile} does not define any packages.`,
    );
  }
  return {
    globs: packages,
    rootDir,
    realRootDir: fs.realpathSync(rootDir),
    type: 'pnpm',
    catalog,
  };
}

function createNpmDefinition({
  globs,
  rootDir,
}: {
  globs: string[];
  rootDir: string;
}): WorkspaceDefinition {
  return {
    globs,
    rootDir,
    realRootDir: fs.realpathSync(rootDir),
    type: 'npm',
  };
}

function chooseWorkspaceDefinition({
  pnpmPreference,
  pnpmDefinition,
  npmDefinition,
}: {
  pnpmPreference: boolean;
  pnpmDefinition?: WorkspaceDefinition;
  npmDefinition?: WorkspaceDefinition;
}): WorkspaceDefinition | undefined {
  if (pnpmDefinition && npmDefinition) {
    const warning =
      'Both pnpm-workspace.yaml and package.json workspaces detected; defaulting to pnpm.';
    if (pnpmPreference) {
      return pnpmDefinition;
    }
    return { ...pnpmDefinition, warning };
  }
  return pnpmDefinition || npmDefinition;
}

function parsePnpmWorkspaceFile(filePath: string): {
  packages: string[];
  catalog: Record<string, string>;
} {
  const content = fs.readFileSync(filePath, 'utf8');
  const parsed = yaml.load(content);

  let packagesField: unknown;
  let catalogField: unknown;
  let catalogsField: unknown;

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const parsedObj = parsed as Record<string, unknown>;
    packagesField = parsedObj.packages;
    catalogField = parsedObj.catalog;
    catalogsField = parsedObj.catalogs;
  } else {
    packagesField = parsed;
  }

  if (!Array.isArray(packagesField)) {
    throw new Error(
      `pnpm workspace file at ${filePath} does not define any packages.`,
    );
  }

  const packages = packagesField
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value): value is string => Boolean(value));

  if (!packages.length) {
    throw new Error(
      `pnpm workspace file at ${filePath} does not define any packages.`,
    );
  }

  // Parse catalog if it exists
  const catalog: Record<string, string> = {};
  if (
    catalogField &&
    typeof catalogField === 'object' &&
    !Array.isArray(catalogField)
  ) {
    for (const [key, value] of Object.entries(catalogField)) {
      if (typeof value === 'string') {
        catalog[key] = value;
      }
    }
  }

  // Parse and flatten named catalogs onto the main catalog
  // Named catalogs are flattened with the key format: "catalogName:packageName"
  if (
    catalogsField &&
    typeof catalogsField === 'object' &&
    !Array.isArray(catalogsField)
  ) {
    for (const [catalogName, catalogContent] of Object.entries(catalogsField)) {
      if (
        catalogContent &&
        typeof catalogContent === 'object' &&
        !Array.isArray(catalogContent)
      ) {
        for (const [packageName, version] of Object.entries(catalogContent)) {
          if (typeof version === 'string') {
            // Flatten named catalog entries with format "catalogName:packageName"
            catalog[`${catalogName}:${packageName}`] = version;
          }
        }
      }
    }
  }

  return { packages, catalog };
}

function workspaceIncludesPath({
  appDir,
  globs,
  rootDir,
}: {
  appDir: string;
  globs: string[];
  rootDir: string;
}) {
  const target = path.resolve(appDir);
  const matches = fastGlob.sync(globs, {
    absolute: true,
    cwd: rootDir,
    onlyDirectories: true,
    followSymbolicLinks: false,
    ignore: ['**/node_modules/**'],
  });
  return matches.some((dir) => path.resolve(dir) === target);
}

function collectWorkspacePackages(
  definition: WorkspaceDefinition,
): Map<string, WorkspacePackage> {
  const { globs, rootDir } = definition;
  const directories = fastGlob.sync(globs, {
    absolute: true,
    cwd: rootDir,
    onlyDirectories: true,
    followSymbolicLinks: false,
    ignore: ['**/node_modules/**'],
  });

  const packages = new Map<string, WorkspacePackage>();

  for (const dir of directories) {
    const packageJsonPath = path.join(dir, 'package.json');
    if (!fs.existsSync(packageJsonPath)) {
      continue;
    }
    const manifest = readPackageJsonIfExists(packageJsonPath);
    if (!manifest || !manifest.name) {
      continue;
    }
    const { name } = manifest;
    if (packages.has(name)) {
      const existing = packages.get(name);
      throw new Error(
        `Duplicate workspace package name "${name}" found at ${existing?.dir} and ${dir}`,
      );
    }
    const absoluteDir = path.resolve(dir);
    const pkg: WorkspacePackage = {
      dir: absoluteDir,
      manifest,
      name,
      realDir: fs.realpathSync(absoluteDir),
    };
    packages.set(name, pkg);
  }

  return packages;
}

function determineBundleClosure({
  appPath,
  appPkgJson,
  workspace,
  workspacePackages,
}: {
  appPath: string;
  appPkgJson: IPackageJson;
  workspace: WorkspaceDefinition;
  workspacePackages: Map<string, WorkspacePackage>;
}): Set<string> {
  const bundled = new Set<string>();
  const queue: DependencyReference[] = [];

  collectDependencyReferences({
    manifest: appPkgJson,
    packageDir: appPath,
    queue,
    workspace,
    workspacePackages,
    sourceName: 'app',
  });

  while (queue.length) {
    const ref = queue.shift();
    if (!ref) {
      continue;
    }
    const targetPackage = workspacePackages.get(ref.name);
    if (!targetPackage) {
      throw new Error(
        `Dependency "${ref.name}" referenced via ${ref.section} is not part of the workspace.`,
      );
    }
    if (bundled.has(targetPackage.name)) {
      continue;
    }
    bundled.add(targetPackage.name);
    collectDependencyReferences({
      manifest: targetPackage.manifest,
      packageDir: targetPackage.dir,
      queue,
      workspace,
      workspacePackages,
      sourceName: targetPackage.name,
    });
  }

  return bundled;
}

function collectDependencyReferences({
  manifest,
  packageDir,
  queue,
  workspace,
  workspacePackages,
  sourceName,
}: {
  manifest: IPackageJson;
  packageDir: string;
  queue: DependencyReference[];
  workspace: WorkspaceDefinition;
  workspacePackages: Map<string, WorkspacePackage>;
  sourceName: string;
}) {
  const sections: DependencySection[] = [
    'dependencies',
    'optionalDependencies',
    'devDependencies',
  ];

  for (const section of sections) {
    const deps = manifest[section];
    if (!deps) continue;
    for (const [depName, spec] of Object.entries(deps)) {
      if (typeof spec !== 'string') continue;
      const target = resolveWorkspaceTarget({
        depName,
        packageDir,
        spec,
        workspace,
        workspacePackages,
        sourceName,
      });
      if (target) {
        queue.push({ name: target, spec, section });
      }
    }
  }
}

function resolveWorkspaceTarget({
  depName,
  packageDir,
  spec,
  workspace,
  workspacePackages,
  sourceName,
}: {
  depName: string;
  packageDir: string;
  spec: string;
  workspace: WorkspaceDefinition;
  workspacePackages: Map<string, WorkspacePackage>;
  sourceName: string;
}): string | undefined {
  if (spec.startsWith('workspace:')) {
    if (!workspacePackages.has(depName)) {
      throw new Error(
        `Workspace dependency "${depName}" referenced by ${sourceName} is not declared in workspace globs.`,
      );
    }
    return depName;
  }

  if (spec.startsWith('file:')) {
    const targetPath = spec.slice('file:'.length);
    const resolved = path.resolve(packageDir, targetPath);
    const targetRealPath = fs.realpathSync(resolved);
    const relativeToRoot = path.relative(workspace.realRootDir, targetRealPath);
    if (relativeToRoot.startsWith('..') || path.isAbsolute(relativeToRoot)) {
      throw new Error(
        `Dependency "${depName}" from ${sourceName} resolves outside of workspace root via spec ${spec}.`,
      );
    }
    for (const pkg of workspacePackages.values()) {
      if (pkg.realDir === targetRealPath) {
        return pkg.name;
      }
    }
    throw new Error(
      `Dependency "${depName}" from ${sourceName} points to ${resolved}, which is not a known workspace package.`,
    );
  }

  return undefined;
}

function getBundlePath(packageName: string): string {
  if (packageName.startsWith('@')) {
    const [scope, ...rest] = packageName.split('/');
    return [scope, rest.join('/')].filter(Boolean).join('/');
  }
  return packageName;
}

function applyRewriteMap(
  manifest: IPackageJson,
  rewriteMap: Record<string, string>,
  catalog?: Record<string, string>,
): IPackageJson {
  const clone = JSON.parse(JSON.stringify(manifest));
  const sections: DependencySection[] = [
    'dependencies',
    'optionalDependencies',
    'devDependencies',
  ];

  for (const section of sections) {
    const sectionDeps = clone[section];
    if (!sectionDeps) continue;

    // Apply path rewrites for workspace packages
    for (const [depName, rewrite] of Object.entries(rewriteMap)) {
      if (sectionDeps[depName]) {
        sectionDeps[depName] = rewrite;
      }
    }

    // Resolve catalog references
    if (catalog) {
      for (const [depName, spec] of Object.entries(sectionDeps)) {
        if (typeof spec === 'string' && spec.startsWith('catalog:')) {
          let catalogKey: string;

          if (spec === 'catalog:') {
            // "catalog:" means use the default catalog with the package name
            catalogKey = depName;
          } else {
            // "catalog:someName" could be either:
            // 1. A named catalog reference (look for "someName:depName")
            // 2. A specific package in default catalog (look for "someName")
            const catalogRef = spec.slice('catalog:'.length);
            const namedCatalogKey = `${catalogRef}:${depName}`;

            // Try named catalog first (e.g., "react17:react")
            if (catalog[namedCatalogKey]) {
              catalogKey = namedCatalogKey;
            } else {
              // Fall back to direct catalog reference
              catalogKey = catalogRef;
            }
          }

          const resolvedVersion = catalog[catalogKey];
          if (resolvedVersion) {
            sectionDeps[depName] = resolvedVersion;
          } else {
            throw new Error(
              `Catalog reference "${spec}" for dependency "${depName}" not found in pnpm catalog. Available catalog entries: ${Object.keys(
                catalog,
              ).join(', ')}`,
            );
          }
        }
      }
    }
  }

  return clone;
}

async function buildPackageEntries({
  bundlePath,
  workspacePackage,
  bundledNames,
  contexts,
  workspace,
}: {
  bundlePath: string;
  workspacePackage: WorkspacePackage;
  bundledNames: Set<string>;
  contexts: Map<string, PackageRewriteContext>;
  workspace: WorkspaceDefinition;
}): Promise<{
  files: ZipEntry[];
  packageJsonEntry: ZipEntry;
}> {
  const packageDir = workspacePackage.dir;
  const { manifest } = workspacePackage;
  const filesToInclude = await gatherPackageFiles({ packageDir, manifest });
  const files: ZipEntry[] = filesToInclude.map((absolutePath) => {
    const relative = path.relative(packageDir, absolutePath);
    const posixRelative = toPosix(path.join(bundlePath, relative));
    return {
      from: absolutePath,
      to: path.posix.join(BUNDLED_ROOT, posixRelative),
    };
  });

  const rewrites = buildRewriteMapForPackage({
    bundlePath,
    manifest,
    bundledNames,
    contexts,
  });

  const packageJsonEntry: ZipEntry = {
    content: Buffer.from(
      `${JSON.stringify(
        applyRewriteMap(manifest, rewrites, workspace.catalog),
        null,
        2,
      )}\n`,
    ),
    to: path.posix.join(BUNDLED_ROOT, bundlePath, 'package.json'),
  };

  return { files, packageJsonEntry };
}

async function gatherPackageFiles({
  packageDir,
  manifest,
}: {
  packageDir: string;
  manifest: IPackageJson;
}): Promise<string[]> {
  const declaredPatterns = Array.isArray((manifest as any).files)
    ? ((manifest as any).files as string[])
    : ['**/*'];

  const normalizedPatterns = declaredPatterns.flatMap((pattern) => {
    if (pattern.includes('*')) {
      return [pattern];
    }
    const absoluteCandidate = path.join(packageDir, pattern);
    try {
      const stats = fs.statSync(absoluteCandidate);
      if (stats.isDirectory()) {
        const posixPattern = pattern.replace(/\\/g, '/');
        return [pattern, `${posixPattern}/**/*`];
      }
    } catch (_error) {
      // ignore stat errors and treat as file pattern
    }
    return [pattern];
  });

  const matchedFiles = await fastGlob(normalizedPatterns, {
    absolute: true,
    cwd: packageDir,
    dot: true,
    onlyFiles: true,
    followSymbolicLinks: false,
    ignore: DEFAULT_IGNORE_GLOBS,
  });

  const extras = [
    'README',
    'README.md',
    'README.txt',
    'LICENSE',
    'LICENCE',
    'NOTICE',
    'CHANGELOG',
    'CHANGELOG.md',
  ];

  for (const extra of extras) {
    const extraPath = path.join(packageDir, extra);
    if (fs.existsSync(extraPath) && fs.statSync(extraPath).isFile()) {
      matchedFiles.push(extraPath);
    }
  }

  const uniqueFiles = Array.from(new Set(matchedFiles));

  return uniqueFiles.filter((filePath) => !filePath.endsWith('package.json'));
}

function buildRewriteMapForPackage({
  bundlePath,
  manifest,
  bundledNames,
  contexts,
}: {
  bundlePath: string;
  manifest: IPackageJson;
  bundledNames: Set<string>;
  contexts: Map<string, PackageRewriteContext>;
}): Record<string, string> {
  const rewriteMap: Record<string, string> = {};
  const sections: DependencySection[] = [
    'dependencies',
    'optionalDependencies',
    'devDependencies',
  ];

  for (const section of sections) {
    const deps = manifest[section];
    if (!deps) continue;
    for (const [depName, spec] of Object.entries(deps)) {
      if (typeof spec !== 'string') continue;
      if (bundledNames.has(depName)) {
        const targetBundlePath =
          contexts.get(depName)?.bundlePath ?? getBundlePath(depName);
        const fromPath = path.posix.join(BUNDLED_ROOT, bundlePath);
        const toPath = path.posix.join(BUNDLED_ROOT, targetBundlePath);
        const relative = path.posix.relative(fromPath, toPath) || '.';
        rewriteMap[depName] = `file:${relative}`;
      }
    }
  }
  return rewriteMap;
}

function toPosix(targetPath: string) {
  return targetPath.split(path.sep).join(path.posix.sep);
}
